import React, { useState } from "react";
import step1 from "./StepsLine_Media/Step41-removebg-preview.png";
import step2 from "./StepsLine_Media/Step22-removebg-preview.png";
import step3 from "./StepsLine_Media/Step23-removebg-preview.png";
import step4 from "./StepsLine_Media/Step24-removebg-preview.png";

import "./StepLine.css";
import { Link } from "react-router-dom";

const stepsData = [
  {
    image: step1,
    badgeText: "Valuation",
    heading: "Set the Right Price",
    description:
      "Evaluate your domain's worth and set a fair, market-driven price.",
    delay: 0,
  },
  {
    image: step2,
    badgeText: "Listing",
    heading: "Gain Maximum Exposure",
    description:
      "List your domain on getBrand.gold and reach verified global buyers.",
    delay: 250,
  },
  {
    image: step3,
    badgeText: "Transaction",
    heading: "Secure the Deal",
    description:
      "Connect with buyers and finalize the sale through safe transactions.",
    delay: 500,
  },
  {
    image: step4,
    badgeText: "Transfer",
    heading: "Complete Ownership Handover",
    description:
      "Transfer the domain securely and receive instant confirmation.",
    delay: 750,
  },
];

const StepsLine = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-16 md:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-blue-100">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#101c4c] leading-tight mb-4">
              Selling your{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#b99653] to-[#fcdb6a]">
                .Gold Domain
              </span>{" "}
              made easy
            </h2>
            <p className="mx-auto text-base sm:text-lg lg:text-xl text-gray-600 mt-4 max-w-3xl leading-relaxed">
              Follow these simple steps to sell your .gold domain with ease and
              confidence.
            </p>
          </div>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8">
          {stepsData.map((step, index) => {
            const isHovered = hoveredIndex === index;

            return (
              <div
                key={index}
                className="group relative flex"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                  animation: `fadeInUp 0.6s ease-out ${step.delay}ms both`,
                }}
              >
                <div
                  className={`
                    relative overflow-hidden rounded-2xl bg-white border border-gray-100
                    transition-all duration-500 ease-out cursor-default w-full
                    flex flex-col
                    ${
                      isHovered
                        ? "shadow-2xl scale-102 -translate-y-3 border-blue-200"
                        : "shadow-lg hover:shadow-xl"
                    }
                  `}
                >
                  {/* Gradient Background on Hover */}
                  <div
                    className={`
                      absolute inset-0 bg-gradient-to-br from-amber-50 via-blue-50 to-indigo-50
                      opacity-0 transition-opacity duration-500
                      ${isHovered ? "opacity-100" : ""}
                    `}
                  />

                  {/* Content */}
                  <div className="relative z-10 p-6 sm:p-8 flex flex-col flex-grow">
                    {/* Step Number Badge */}
                    <div
                      className={`
                        inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-5
                        font-bold text-2xl transition-all duration-500 shadow-md
                        ${
                          isHovered
                            ? "bg-[#101c4c] text-white scale-110 shadow-xl"
                            : "bg-gradient-to-br from-[#fcdb6a] to-[#b99653] text-[#101c4c]"
                        }
                      `}
                    >
                      {index + 1}
                    </div>

                    {/* Image Container - Fixed Height for Alignment */}
                    <div
                      className="mb-6 mx-auto w-full flex items-center justify-center"
                      style={{ height: "160px" }}
                    >
                      <img
                        src={step.image}
                        className={`
                          max-h-full max-w-full object-contain drop-shadow-xl
                          transition-all duration-500
                          ${isHovered ? "scale-110 drop-shadow-2xl" : ""}
                        `}
                        alt={`${step.badgeText} step`}
                      />
                    </div>

                    {/* Badge */}
                    <div className="mb-5">
                      <span
                        className={`
                          inline-flex px-5 py-2 rounded-full font-semibold text-sm tracking-wide
                          transition-all duration-500 shadow-md
                          ${
                            isHovered
                              ? "bg-[#101c4c] text-white shadow-lg scale-105"
                              : "bg-gradient-to-r from-[#fcdb6a] to-[#b99653] text-[#101c4c]"
                          }
                        `}
                      >
                        {step.badgeText}
                      </span>
                    </div>

                    {/* Heading - Fixed Min Height for Alignment */}
                    <h3
                      className={`
                        text-xl sm:text-2xl font-bold mb-4 leading-snug
                        transition-colors duration-500
                        ${isHovered ? "text-[#101c4c]" : "text-[#0d1636]"}
                      `}
                      style={{ minHeight: "3.5rem" }}
                    >
                      {step.heading}
                    </h3>

                    {/* Description - Flex Grow to Fill Space */}
                    <p
                      className={`
                        text-sm sm:text-base leading-relaxed flex-grow
                        transition-colors duration-500
                        ${isHovered ? "text-gray-700" : "text-gray-600"}
                      `}
                    >
                      {step.description}
                    </p>
                  </div>

                  {/* Decorative Bottom Corner Element */}
                  <div
                    className={`
                      absolute -bottom-20 -right-20 w-40 h-40 rounded-full
                      bg-gradient-to-br from-[#fcdb6a]/30 to-[#101c4c]/20 blur-3xl
                      transition-all duration-500
                      ${isHovered ? "scale-150 opacity-60" : "opacity-20"}
                    `}
                  />

                  {/* Top Corner Accent */}
                  <div
                    className={`
                      absolute -top-10 -left-10 w-32 h-32 rounded-full
                      bg-gradient-to-br from-blue-400/20 to-transparent blur-2xl
                      transition-all duration-500
                      ${isHovered ? "scale-125 opacity-50" : "opacity-10"}
                    `}
                  />

                  {/* Connection Line (Desktop Only) */}
                  {index < stepsData.length - 1 && (
                    <div className="hidden xl:block absolute top-1/2 -right-4 z-30">
                      <div className="flex items-center gap-0">
                        <div
                          className={`
                            w-8 h-0.5 bg-gradient-to-r from-[#b99653] via-[#fcdb6a] to-[#b99653]
                            transition-all duration-500
                            ${isHovered ? "opacity-100 w-10" : "opacity-40"}
                          `}
                        />
                        <div
                          className={`
                            w-2 h-2 rounded-full bg-[#fcdb6a] shadow-md
                            transition-all duration-500
                            ${isHovered ? "scale-150 bg-[#b99653]" : ""}
                          `}
                        />
                      </div>
                    </div>
                  )}

                  {/* Hover Border Glow */}
                  <div
                    className={`
                      absolute inset-0 rounded-2xl border-2 border-[#b99653]
                      opacity-0 transition-opacity duration-500
                      ${isHovered ? "opacity-30" : ""}
                    `}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-lg p-8 border border-blue-100 text-center">
          <h3 className="text-2xl font-bold text-[#101c4c] mb-3">
            Ready to sell your domain?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Join thousands of sellers who have successfully sold their premium
            .gold domains on our platform.
          </p>
          <Link to="/post-dot-gold-domain">
            <button className="bg-gradient-to-r from-[#b99653] to-[#fcdb6a] text-white px-8 py-3 rounded-xl hover:shadow-xl transition-all duration-300 font-semibold hover:scale-105">
              Get Started Now
            </button>
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default StepsLine;
